/* Mi perfil → Información personal (la persona; constante entre empresas). */
import { useEffect, useRef, useState } from 'react'
import { useApi } from '@/hooks/useApi'
import { useAuth } from '@/hooks/useAuth'
import { showToast } from '@/components/layout/Toast'
import { usePerfilCtx } from './PortalPerfilLayout'
import { IconCamera } from '@/components/icons/Icons'

/** Dos iniciales del nombre, o una del correo. Mismo recurso que el sidebar. */
function iniciales(nombre: string, email: string): string {
  const partes = nombre.trim().split(/\s+/).filter(Boolean)
  if (partes.length >= 2) return (partes[0][0] + partes[1][0]).toUpperCase()
  if (partes.length === 1 && partes[0].length > 1) return partes[0].slice(0, 2).toUpperCase()
  return (email[0] || '?').toUpperCase()
}

export default function PerfilPersonal() {
  const api = useApi()
  const { perfil, loading, reload } = usePerfilCtx()
  const { refreshUser } = useAuth()

  const [nombre, setNombre] = useState('')
  const [telefono, setTelefono] = useState('')
  const [saving, setSaving] = useState(false)
  const [subiendoFoto, setSubiendoFoto] = useState(false)
  const inputFoto = useRef<HTMLInputElement>(null)

  // Seed local desde el perfil compartido cada vez que carga/cambia.
  useEffect(() => {
    setNombre(perfil?.personal?.nombre || '')
    setTelefono(perfil?.personal?.telefono || '')
  }, [perfil])

  async function guardar() {
    if (!nombre.trim()) { showToast('El nombre no puede quedar vacío', 'error'); return }
    setSaving(true)
    try {
      await api.put('/api/portal/perfil', { nombre: nombre.trim(), telefono: telefono.trim() })
      showToast('Datos personales actualizados', 'success')
      reload()
      // El pie del sidebar lee el nombre de `/api/auth/me`: sin esto se queda
      // con el anterior hasta recargar la página entera.
      refreshUser?.()
    } catch (err: any) {
      showToast(err.message || 'No se pudo guardar', 'error')
    } finally { setSaving(false) }
  }

  async function subirFoto(file: File | null) {
    if (!file) return
    setSubiendoFoto(true)
    try {
      const fd = new FormData()
      fd.append('file', file)
      await api.upload('/api/auth/perfil/foto', fd)
      showToast('Foto actualizada', 'success')
      reload()
      refreshUser?.()
    } catch (err: any) {
      showToast(err.message || 'No se pudo subir la foto', 'error')
    } finally {
      setSubiendoFoto(false)
      if (inputFoto.current) inputFoto.current.value = ''
    }
  }

  if (loading && !perfil) return <p className="pp-cargando">Cargando…</p>

  const email = perfil?.email || ''
  const foto = perfil?.personal?.foto_url || null

  return (
    <div className="card pp-card">
      <div className="pp-card__head">
        <div>
          <h3 className="pp-card__titulo">Mi información personal</h3>
          <p className="pp-card__sub">Son tuyos y no cambian según la empresa.</p>
        </div>
      </div>

      {/* La foto primero: es lo que identifica la pantalla como tuya, y es
          además lo que se ve en el pie del sidebar desde que se sube. */}
      <div className="pp-identidad">
        <button
          type="button"
          className="pp-avatar"
          onClick={() => inputFoto.current?.click()}
          disabled={subiendoFoto}
          title="Cambiar la foto"
        >
          {foto
            ? <img src={foto} alt="" className="pp-avatar__img" />
            : <span className="pp-avatar__iniciales">{iniciales(nombre, email)}</span>}
          <span className="pp-avatar__capa"><IconCamera size={16} /></span>
        </button>
        <div className="pp-identidad__texto">
          <strong>{nombre || email}</strong>
          <span>{subiendoFoto ? 'Subiendo…' : 'PNG, JPG o WebP, hasta 5 MB'}</span>
          <button
            type="button"
            className="btn btn-sm btn-secondary"
            onClick={() => inputFoto.current?.click()}
            disabled={subiendoFoto}
          >
            {foto ? 'Cambiar foto' : 'Subir foto'}
          </button>
        </div>
        <input
          ref={inputFoto}
          type="file"
          accept="image/png,image/jpeg,image/webp"
          className="pp-input-oculto"
          onChange={e => subirFoto(e.target.files?.[0] ?? null)}
        />
      </div>

      <div className="pp-grid-campos">
        <div className="form-group">
          <label className="form-label">Nombre y apellido</label>
          <input className="form-input" value={nombre} onChange={e => setNombre(e.target.value)} placeholder="Tu nombre completo" />
        </div>
        <div className="form-group">
          <label className="form-label">Correo (login)</label>
          <input className="form-input" value={email} disabled />
        </div>
        <div className="form-group pp-ancho">
          <label className="form-label">Teléfono / WhatsApp</label>
          <input className="form-input" value={telefono} onChange={e => setTelefono(e.target.value)} placeholder="+51 999 999 999" />
        </div>
      </div>

      <div className="pp-card__pie">
        <button className="btn btn-primary" onClick={guardar} disabled={saving}>
          {saving ? 'Guardando…' : 'Guardar datos personales'}
        </button>
      </div>
    </div>
  )
}
